/**
 * Batch 27h — Reinstate-tenant dialog (reason required).
 *
 * Spec L1418 calls this ``reinstate``; never ``reactivate``.
 */
import { useState } from "react";
import {
  useReinstateTenant,
  type TenantDetail,
} from "@/hooks/queries/admin/useAdminTenants";
import { useToastStore } from "@/stores/useToastStore";

interface Props {
  tenant: TenantDetail;
  onClose: () => void;
}

export function ReinstateTenantDialog({
  tenant,
  onClose,
}: Props): JSX.Element {
  const [reason, setReason] = useState("");
  const [errMsg, setErrMsg] = useState<string | null>(null);
  const reinstate = useReinstateTenant(tenant.id);
  const addToast = useToastStore((s) => s.addToast);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrMsg(null);
    const trimmed = reason.trim();
    if (trimmed.length === 0) {
      setErrMsg("Reason is required.");
      return;
    }
    reinstate.mutate(
      { reason: trimmed },
      {
        onSuccess: () => {
          addToast(`Tenant reinstated: ${tenant.name}`, "success");
          onClose();
        },
        onError: (err) => {
          setErrMsg(err.message || "Could not reinstate tenant.");
        },
      },
    );
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
    >
      <form
        onSubmit={submit}
        className="w-full max-w-md space-y-3 rounded-xl border border-[var(--p-slate-150)] bg-white p-5 shadow-card dark:border-white/[0.06] dark:bg-surface-card dark:shadow-card-dark"
      >
        <h2 className="text-base font-semibold text-text-primary dark:text-text-primary">
          Reinstate {tenant.name}?
        </h2>
        <p className="text-sm text-text-secondary dark:text-brand-100/70">
          Users will be able to log in again. Subscription is{" "}
          <strong className="text-text-primary dark:text-text-primary">not</strong>{" "}
          auto-restored — the tenant must re-subscribe through the normal
          flow if they want renewed access to AI actions.
        </p>
        <label className="block text-sm">
          <span className="block text-text-secondary dark:text-brand-100/70">
            Reason (required)
          </span>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
            maxLength={1024}
            rows={3}
            className="mt-1 w-full rounded-md border border-[var(--p-slate-150)] bg-white px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-white/10 dark:bg-surface-canvas dark:text-text-primary dark:focus:ring-brand-teal"
            placeholder="e.g. Ticket-1234 resolved; payment cleared"
          />
        </label>
        {errMsg ? (
          <p className="text-sm text-red-600 dark:text-red-400">{errMsg}</p>
        ) : null}
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-brand-200 px-3 py-1.5 text-sm font-medium text-text-secondary hover:bg-surface-canvas dark:border-white/[0.08] dark:text-brand-100/80 dark:hover:bg-white/[0.04]"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={reinstate.isPending}
            className="rounded bg-accent-primary-hover px-3 py-1.5 text-sm font-medium text-white hover:bg-surface-elevated disabled:opacity-50 dark:bg-accent-primary dark:hover:bg-accent-primary-hover"
          >
            {reinstate.isPending ? "Reinstating…" : "Reinstate"}
          </button>
        </div>
      </form>
    </div>
  );
}
